import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Link, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Boxes, Bot, ChevronDown, ChevronsUpDown, Eye, LayoutGrid, LogOut, PanelLeftClose, PanelLeftOpen, Plug, PlayCircle, Search, Sparkles, UsersRound, Zap, ArrowLeftRight } from "lucide-react";
import { GrowthOSWordmark } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatRoleLabel } from "@/components/common";
import { buildWorkspaceOptions, clientInitials, matchesWorkspaceQuery } from "@/components/workspaceOptions";
import { AgentPicture } from "@/components/AgentPicture";
import { ChatWorkspaceProvider, useChatShell } from "@/components/chat/ChatWorkspaceContext";
import AgentChat from "@/components/chat/AgentChat";
import AgentNavigation from "@/components/agent-workspace/AgentNavigation";
import HubUpdates from "@/components/updates/HubUpdates";
import { ViewAsBanner, ViewAsPicker } from "@/components/viewAs/ViewAs";

import offersPilot from "./offers/pilot.json";
import TestNavigation from "./growth/TestNavigation";
import { growthEnabled } from "@/growth/pilot";

export { buildWorkspaceOptions, clientInitials };

export const NAV = [
  { to: "/offers", label: "Offers", icon: Boxes, pilot: true },
  { to: "/playbooks", label: "Get Better Results", icon: Sparkles },
  { to: "/connections", label: "Connections", icon: Plug },
  { to: "/alloy", label: "GrowthOS Alloy", icon: Zap },
  { to: "/team", label: "Team", icon: UsersRound },
];

export const navigationForClient = (clientSlug) => NAV.filter(item => !item.pilot || offersPilot.clientSlugs.includes(clientSlug));

export function WorkspaceSwitcher({ activeClient, memberships = [], selectClient, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const menuContentRef = useRef(null);
  const searchInputRef = useRef(null);
  const resultCountId = useId();
  const clientName = activeClient?.clientName ?? "Client workspace";
  const options = buildWorkspaceOptions(memberships, activeClient);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredOptions = options.filter((workspace) => matchesWorkspaceQuery(workspace, normalizedQuery));

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) setSearchQuery("");
  };

  const handleWorkspaceChange = (clientSlug) => {
    if (!clientSlug || clientSlug === activeClient?.clientSlug) return;
    selectClient(clientSlug).catch(() => undefined);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleOpenChange(false);
      return;
    }

    const boundary = ["ArrowDown", "Home"].includes(event.key)
      ? "first"
      : ["ArrowUp", "End"].includes(event.key)
        ? "last"
        : null;
    if (boundary) {
      event.preventDefault();
      event.stopPropagation();
      const items = [...(menuContentRef.current?.querySelectorAll('[role="menuitemradio"]') ?? [])];
      const target = boundary === "first" ? items[0] : items[items.length - 1];
      target?.focus();
      return;
    }

    event.stopPropagation();
  };

  const handleResultKeyDown = (event, index) => {
    const returnToSearch = (event.key === "Tab" && event.shiftKey)
      || (event.key === "ArrowUp" && index === 0);
    if (!returnToSearch) return;
    event.preventDefault();
    event.stopPropagation();
    searchInputRef.current?.focus();
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="brand-switcher"
          type="button"
          aria-label={`Switch workspace. Current workspace: ${clientName}`}
          className="mb-2 flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2.5 text-left transition-colors hover:border-white/20 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent/70"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#22C55E]/15 font-display text-xs font-bold text-[#22C55E]">
              <AgentPicture src={activeClient?.agentImageUrl} fallback={clientInitials(clientName)} />
            </span>
            <div className="min-w-0">
              <div className="max-w-[130px] truncate text-xs font-semibold">{clientName}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-white/40">workspace</div>
            </div>
          </div>
          <ChevronsUpDown size={14} aria-hidden="true" className="ml-2 shrink-0 text-white/45" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={menuContentRef}
        side="top"
        align="start"
        sideOffset={8}
        data-testid="workspace-menu"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
        className="w-[224px] max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] border-sm-ink/10 bg-white text-sm-ink shadow-xl"
      >
        <DropdownMenuLabel className="sticky top-0 z-10 border-b border-sm-ink/10 bg-white px-2 pb-2 pt-2">
          <div className="text-xs font-semibold text-sm-ink">Switch brand agent</div>
          <div
            id={resultCountId}
            data-testid="workspace-result-count"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-0.5 text-[10px] font-normal text-sm-muted"
          >
            {normalizedQuery
              ? `${filteredOptions.length} of ${options.length} workspaces`
              : `${options.length} ${options.length === 1 ? "workspace" : "workspaces"} available`}
          </div>
          <div className="relative mt-2">
            <Search size={13} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm-muted" />
            <input
              ref={searchInputRef}
              data-testid="workspace-search"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label="Search workspaces"
              aria-describedby={resultCountId}
              placeholder="Search workspaces"
              autoComplete="off"
              className="h-8 w-full rounded-md border border-sm-ink/15 bg-sm-surface pl-8 pr-2 text-xs font-normal text-sm-ink outline-none placeholder:text-sm-muted focus:border-sm-primary/50 focus:ring-2 focus:ring-sm-primary/15"
            />
          </div>
        </DropdownMenuLabel>
        {filteredOptions.length > 0 ? (
          <DropdownMenuRadioGroup value={activeClient?.clientSlug ?? ""} onValueChange={handleWorkspaceChange}>
            {filteredOptions.map((workspace, index) => (
              <Fragment key={workspace.clientSlug}>
                {!normalizedQuery && workspace.kind === "test" && index > 0 && filteredOptions[index - 1].kind !== "test" ? (
                  <div data-testid="workspace-test-group" className="px-2 pb-1 pt-2 font-mono text-[9px] uppercase tracking-wider text-sm-muted">
                    Test &amp; sandbox
                  </div>
                ) : null}
                <DropdownMenuRadioItem
                  value={workspace.clientSlug}
                  data-testid={`workspace-option-${workspace.clientSlug}`}
                  onKeyDown={(event) => handleResultKeyDown(event, index)}
                  className="items-center gap-2.5 py-2 pl-7 pr-2 focus:bg-sm-ink/5 focus:text-sm-ink"
                >
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-display text-[10px] font-bold ${workspace.active ? "bg-[#22C55E]/15 text-[#16A34A]" : workspace.kind === "test" ? "bg-sm-ink/5 text-sm-muted" : "bg-sm-primary/10 text-sm-primary"}`}>
                    <AgentPicture src={workspace.agentImageUrl} fallback={workspace.initials} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold">{workspace.clientName}</span>
                    <span className="block truncate text-[10px] text-sm-muted">
                      {workspace.roleLabel}
                      {workspace.showSlug ? <span className="font-mono"> · {workspace.clientSlug}</span> : null}
                    </span>
                  </span>
                </DropdownMenuRadioItem>
              </Fragment>
            ))}
          </DropdownMenuRadioGroup>
        ) : (
          <div data-testid="workspace-empty" role="status" className="px-3 py-5 text-center text-xs text-sm-muted">
            No workspaces found
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LayoutShell() {
  const { name: agentName, fullscreen } = useChatShell();
  const { pathname } = useLocation();
  const isChat = pathname.replace(/\/+$/, "") === "/agent/chat";
  const [collapsedOutsideChat, setCollapsedOutsideChat] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarKeyboardFocus, setSidebarKeyboardFocus] = useState(false);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [sidebarUserMenuOpen, setSidebarUserMenuOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // Chat gets more room on each visit, without changing the rest of the dashboard.
  // Its manual expansion lasts until leaving Chat; non-Chat pages keep their preference.
  useEffect(() => {
    if (!isChat) setChatExpanded(false);
  }, [isChat]);
  const sidebarCollapsed = isChat ? !chatExpanded : collapsedOutsideChat;
  const collapsed = sidebarCollapsed && !(sidebarHovered || sidebarKeyboardFocus || workspaceMenuOpen || sidebarUserMenuOpen);
  const toggleSidebar = () => {
    if (isChat) setChatExpanded((expanded) => !expanded);
    else setCollapsedOutsideChat((value) => !value);
    setSidebarHovered(false);
    setSidebarKeyboardFocus(false);
  };
  const { signOut, activeClient, memberships, selectClient, chooseWorkspace, member, demoView, exitDemoView, canViewAs, viewingAs, stopViewAs } = useApp();
  const navigate = useNavigate();
  const clientName = activeClient?.clientName ?? "Client workspace";
  const userLabel = member?.email?.split("@")[0] ?? "Member";
  const userInitials = userLabel.slice(0, 2).toUpperCase();
  // All brands lists a person's brand workspaces side by side, so it exists once there are two of them. The fixture
  // tenants every operator holds are not brands (workspaceOptions.js) and do not open it.
  const brandCount = buildWorkspaceOptions(memberships, activeClient).filter((option) => option.kind === "brand").length;
  const showAllBrands = brandCount > 1;
  const onAllBrands = pathname.replace(/\/+$/, "") === "/brands";
  // View as: offered to staff (the server says who) while they are not already looking as someone; never in the demo.
  const offerViewAs = Boolean(canViewAs) && !viewingAs && !demoView;
  const stopLooking = () => { Promise.resolve(stopViewAs?.()).catch(() => undefined); };
  const allBrandsClass = (isActive, mobile = false) => (mobile
    ? `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-white/10 text-white" : "text-white/50"}`
    : `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "border border-sm-accent/50 bg-white/10 text-white" : "border border-transparent text-white/55 hover:bg-white/5 hover:text-white"
    } ${collapsed ? "justify-center px-0" : ""}`);

  return (
    <div className="flex min-h-screen bg-sm-surface text-sm-ink">
      <aside
        data-testid="sidebar"
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        onFocusCapture={(event) => {
          if (event.currentTarget.contains(event.target) && event.target.matches(":focus-visible")) setSidebarKeyboardFocus(true);
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setSidebarKeyboardFocus(false);
        }}
        style={fullscreen ? { display: "none" } : undefined}
        className={`mc-bg fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-white/10 text-white transition-all duration-300 md:flex ${collapsed ? "w-[72px]" : "w-[248px]"}`}
      >
        <div className={`flex h-16 items-center border-b border-white/10 ${collapsed ? "justify-center px-2" : "justify-between px-5"}`}>
          {!collapsed && (
            <div>
              <GrowthOSWordmark className="text-xl" />
              <div className="label-eyebrow mt-0.5 text-[9px] text-sm-accent">Brand Agent</div>
            </div>
          )}
          <button
            data-testid="sidebar-collapse-btn"
            type="button"
            aria-label={sidebarCollapsed ? collapsed ? "Expand sidebar" : "Keep sidebar open" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            onClick={toggleSidebar}
            className="rounded-md p-1.5 text-white/50 transition-colors hover:bg-white/5 hover:text-white"
          >
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {showAllBrands ? (
            <NavLink to="/brands" data-testid="nav-all-brands" title={collapsed ? "All brands" : undefined} className={({ isActive }) => allBrandsClass(isActive)}>
              <LayoutGrid size={17} className="shrink-0" aria-hidden="true" />
              {!collapsed && (
                <>
                  <span>All brands</span>
                  <span className="ml-auto rounded-full bg-sm-accent/20 px-2 py-px text-[11px] font-bold text-[#CFE3FB]" aria-label={`${brandCount} brands`}>{brandCount}</span>
                </>
              )}
            </NavLink>
          ) : null}
          {showAllBrands && !collapsed ? (
            <div data-testid="sidebar-workspace-group" className="label-eyebrow truncate px-3 pb-1 pt-3.5 text-[10px] text-white/60">{clientName}</div>
          ) : null}
          <AgentNavigation collapsed={collapsed} agentName={agentName} />
          {growthEnabled(activeClient?.clientSlug) ? <TestNavigation collapsed={collapsed} /> : null}
          {navigationForClient(activeClient?.clientSlug).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "border border-sm-accent/50 bg-white/10 text-white" : "border border-transparent text-white/55 hover:bg-white/5 hover:text-white"
                } ${collapsed ? "justify-center px-0" : ""}`
              }
              title={collapsed ? label : undefined}
            >
              <Icon size={17} className="shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3">
          {/* Staff only (the server says who): look at the dashboard as one of this brand's people. Above the
              workspace and account cards, so it sits where the brand and the person are chosen. */}
          {offerViewAs ? (
            <button
              type="button"
              data-testid="sidebar-view-as"
              onClick={() => setViewAsOpen(true)}
              title={collapsed ? "View as…" : undefined}
              aria-label="View the dashboard as a member of this brand"
              className={`mb-2 flex w-full items-center gap-2.5 rounded-lg border border-sm-accent/40 bg-sm-accent/10 px-3 py-2.5 text-left transition-colors hover:border-sm-accent/70 hover:bg-sm-accent/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent/70 ${collapsed ? "justify-center px-0" : ""}`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sm-accent/20 text-[#9cc6f0]"><Eye size={15} aria-hidden="true" /></span>
              {!collapsed && (
                <span className="min-w-0">
                  <span className="block truncate text-xs font-semibold">View as…</span>
                  <span className="block font-mono text-[9px] uppercase tracking-wider text-white/40">staff only · read only</span>
                </span>
              )}
            </button>
          ) : null}
          {!collapsed && (
            <WorkspaceSwitcher
              activeClient={activeClient}
              memberships={memberships}
              selectClient={selectClient}
              onOpenChange={setWorkspaceMenuOpen}
            />
          )}
          <DropdownMenu onOpenChange={setSidebarUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="sidebar-user-menu"
                className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-white/5 ${collapsed ? "justify-center" : ""}`}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-sm-accent/40 bg-sm-accent/20 text-xs font-bold text-[#9cc6f0]">{userInitials}</span>
                {!collapsed && (
                  <div className="min-w-0 text-left">
                    <div className="truncate text-xs font-semibold">{userLabel}</div>
                    <div className="text-[10px] text-white/45">{formatRoleLabel(activeClient?.role)}</div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-52 border-sm-ink/10 bg-white text-sm-ink">
              <DropdownMenuLabel className="text-xs text-sm-muted">{member?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-sm-ink/10" />
              <DropdownMenuItem onClick={() => navigate("/")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                <Bot size={14} /> {agentName}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/connections")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                <Plug size={14} /> Connections
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/team")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                <UsersRound size={14} /> Team
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="sign-out-btn"
                onClick={() => { signOut(); navigate("/login"); }}
                className="gap-2 text-sm text-[#EF4343] focus:bg-[#EF4343]/10 focus:text-[#EF4343]"
              >
                <LogOut size={14} /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ${fullscreen ? "" : sidebarCollapsed ? "md:ml-[72px]" : "md:ml-[248px]"}`}>
        <div className="sticky top-0 z-30">
        {/* The banner stays while staff look, even with the chat in focus and the header away. */}
        <ViewAsBanner viewingAs={viewingAs} onStop={stopLooking} />
        <header style={fullscreen ? { display: "none" } : undefined} className="flex h-16 items-center justify-between gap-3 border-b border-white/10 bg-[#0F2442]/95 px-4 text-white backdrop-blur-md md:px-8">
          <div className="flex items-center gap-3 md:hidden">
            <GrowthOSWordmark className="text-lg" />
          </div>
          <div className="hidden items-center gap-2 md:flex" data-testid="agent-status-badge">
            <span className="inline-flex items-center gap-2 rounded-full border border-sm-accent/35 bg-sm-accent/10 px-3 py-1 text-xs font-medium text-[#9cc6f0]">
              {onAllBrands && showAllBrands ? <LayoutGrid size={12} aria-hidden="true" /> : <Bot size={12} />}
              {onAllBrands && showAllBrands ? `All brands · ${brandCount} workspaces` : `${agentName} · ${clientName}`}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            {/* Release notes never pop open on a look: staff came to see the person's dashboard, not the changelog. */}
            <HubUpdates viewer={member?.id} autoOpen={!fullscreen && !demoView && !viewingAs} />
            <Link data-testid="header-connections" to="/connections" className="hidden h-9 items-center gap-2 rounded-md border border-white/15 bg-white/[0.06] px-3 text-xs text-white/70 transition-colors hover:border-white/30 hover:text-white sm:flex">
              <Plug size={13} className="text-white/55" /> Connections
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-user-menu" className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.06] py-1.5 pl-1.5 pr-2.5 transition-colors hover:border-white/30">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-sm-accent/40 bg-sm-accent/20 text-[10px] font-bold text-[#9cc6f0]">{userInitials}</span>
                  <ChevronDown size={13} className="text-white/40" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 border-sm-ink/10 bg-white text-sm-ink">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{member?.email}</div>
                  <div className="text-xs font-normal text-sm-muted">{formatRoleLabel(activeClient?.role)} · {clientName}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-sm-ink/10" />
                <DropdownMenuItem onClick={() => navigate("/")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                  <Bot size={14} /> {agentName}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/connections")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                  <Plug size={14} /> Connections
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/team")} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                  <UsersRound size={14} /> Team
                </DropdownMenuItem>
                {memberships.length > 1 ? (
                  <DropdownMenuItem
                    data-testid="header-switch-workspace"
                    onClick={() => { chooseWorkspace(); navigate("/"); }}
                    className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink"
                  >
                    <ArrowLeftRight size={14} /> Switch workspace
                  </DropdownMenuItem>
                ) : null}
                {offerViewAs ? (
                  <DropdownMenuItem data-testid="header-view-as" onSelect={() => setViewAsOpen(true)} className="gap-2 text-sm focus:bg-sm-ink/5 focus:text-sm-ink">
                    <Eye size={14} /> View as…
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  data-testid="header-sign-out"
                  onClick={() => { signOut(); navigate("/login"); }}
                  className="gap-2 text-sm text-[#EF4343] focus:bg-[#EF4343]/10 focus:text-[#EF4343]"
                >
                  <LogOut size={14} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        </div>

        <nav style={fullscreen ? { display: "none" } : undefined} className="flex gap-1 overflow-x-auto border-b border-white/10 bg-sm-ink px-2 py-1.5 md:hidden">
          {showAllBrands ? (
            <NavLink to="/brands" data-testid="mobile-nav-all-brands" className={({ isActive }) => allBrandsClass(isActive, true)}>
              All brands
            </NavLink>
          ) : null}
          <AgentNavigation mobile agentName={agentName} />
          {growthEnabled(activeClient?.clientSlug) ? <TestNavigation mobile /> : null}
          {navigationForClient(activeClient?.clientSlug).map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-white/10 text-white" : "text-white/50"}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <main className={fullscreen ? "min-w-0 flex-1" : "min-w-0 flex-1 px-4 py-6 md:px-8 md:py-8"}>
          {demoView ? (
            <div
              data-testid="demo-view-banner"
              role="status"
              className="mb-6 flex flex-col gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/[0.08] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-2.5">
                <PlayCircle size={16} className="mt-0.5 shrink-0 text-[#D97706]" />
                <p className="text-xs leading-relaxed text-sm-ink/70">
                  <span className="font-semibold text-sm-ink">Demo view.</span> Everything on this screen is sample data for
                  design review. No workspace source is being read and no action can be sent.
                </p>
              </div>
              <button
                data-testid="demo-view-exit"
                type="button"
                onClick={() => { exitDemoView(); navigate("/login"); }}
                className="shrink-0 self-start rounded-full border border-sm-ink/15 bg-white px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-sm-ink/70 transition-colors hover:border-sm-ink/30 sm:self-auto"
              >
                Exit demo
              </button>
            </div>
          ) : null}
          <Outlet />
        </main>
      </div>
      <AgentChat />
      <ViewAsPicker open={viewAsOpen} onOpenChange={setViewAsOpen} />
    </div>
  );
}

export default function Layout() {
  return <ChatWorkspaceProvider><LayoutShell /></ChatWorkspaceProvider>;
}
